/**
 * OverviewScreen — the read-only L1A "calm snapshot" (mockup #s-overview).
 *
 * Assembled entirely from data already loaded in ProjectDetail — no new reads,
 * no new store. Three cards:
 *   1. Next action  — the first actionable stage + a single green CTA.
 *   2. At a glance  — project facts + the money picture (Fee/Paid/Due). This is
 *                     where the money chip relocates to from the header; the
 *                     `data-testid="l1a-money-chip"` is preserved so existing
 *                     tests pass.
 *   3. Pipeline     — the 12 stages, read-only reference (the bar above is the
 *                     navigation; these rows are NOT clickable).
 *
 * Pass 1 feeds an all-`todo` status stub; the real per-stage dots/pills land in
 * pass 4 (deriveL1AStageStates). The component is status-driven, so the visuals
 * upgrade automatically once the real feed replaces the stub.
 */
import StageScreen from './StageScreen';
import { Box, Btn3, Card, Tag } from '../ui';
import { money } from '../../lib/moneyModel';
import type { Currency } from '../../lib/firms';
import {
  L1A_TAB_ITEMS,
  defaultL1ATab,
  type L1ATabKey,
  type StageStatus,
} from '../../lib/l1aTabs';
import type { MoneyView } from '../../lib/trackDemo';

const STAGE_ITEMS = L1A_TAB_ITEMS.filter((i) => !i.meta);

const STATUS_LABEL: Record<StageStatus, string> = {
  done: 'done',
  now: 'in progress',
  todo: 'not started',
};

export default function OverviewScreen({
  clientName,
  vp,
  tier,
  assigneeName,
  candidateCount,
  money: m,
  currency,
  statusByKey,
  onGo,
}: {
  clientName: string;
  vp: string;
  tier: string;
  assigneeName: string;
  candidateCount: number;
  money: MoneyView;
  currency?: Currency;
  statusByKey: Record<string, StageStatus>;
  onGo: (key: L1ATabKey) => void;
}) {
  const cur = currency ?? 'INR';
  const nextKey = defaultL1ATab(statusByKey);
  const nextItem = STAGE_ITEMS.find((i) => i.key === nextKey);
  const nextStatus = nextItem ? statusByKey[nextItem.key] ?? 'todo' : undefined;

  return (
    <StageScreen
      title="Overview"
      tabKey="overview"
      testId="stage-screen-overview"
      sub="A calm snapshot — click any stage above to open its screen"
    >
      {/* 1 · Next action — the single CTA on the page */}
      {nextItem && (
        <Box title="Next action" note="Where your attention is needed">
          <Card flat className="row-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <b>{nextItem.full}</b>
              {nextStatus && (
                <span className="kv" style={{ border: 'none', padding: 0 }}>
                  <span className={`st ${nextStatus}`}>{STATUS_LABEL[nextStatus]}</span>
                </span>
              )}
            </div>
            <div style={{ marginTop: 10 }}>
              <Btn3 intent="grn" size="sm" onClick={() => onGo(nextItem.key)}>
                Open {nextItem.full} →
              </Btn3>
            </div>
          </Card>
        </Box>
      )}

      {/* 2 · At a glance — facts + money (the relocated money chip lives here) */}
      <Box title="At a glance" note="Project facts and the money picture">
        <div className="l1a-screen">
          <div className="kv">
            <span className="k">Client</span>
            <b>{clientName}</b>
          </div>
          <div className="kv">
            <span className="k">VP</span>
            <b>{vp}</b>
          </div>
          <div className="kv">
            <span className="k">Tier</span>
            <b>{tier}</b>
          </div>
          <div className="kv">
            <span className="k">Assignee</span>
            <b>{assigneeName}</b>
          </div>
          <div className="kv">
            <span className="k">Candidate properties</span>
            <b>{candidateCount} linked</b>
          </div>
          <div className="kv">
            <span className="k">Money</span>
            <span className="money-trio" data-testid="l1a-money-chip">
              <span style={{ color: 'var(--ink-soft)' }}>
                Fee <b style={{ color: 'var(--ink)' }}>{money(m.valueINR, cur)}</b>
              </span>
              <span style={{ color: 'var(--good)' }}>
                Paid <b>{money(m.paidINR, cur)}</b>
              </span>
              <span style={{ color: m.dueINR > 0 ? 'var(--warn)' : 'var(--ink-soft)' }}>
                Due <b>{money(m.dueINR, cur)}</b>
              </span>
            </span>
          </div>
        </div>
      </Box>

      {/* 3 · Pipeline — read-only reference list of the 11 stages */}
      <Box
        title="Pipeline"
        note="The 12 L1A stages, listed for reference. Read-only — use the bar above to work in a stage."
      >
        <div className="l1a-screen">
          {STAGE_ITEMS.map((it, idx) => {
            const status = statusByKey[it.key] ?? 'todo';
            return (
              <div className="kv" key={it.key}>
                <span className="k">
                  {idx + 1}. {it.full}
                </span>
                <Tag kind={status === 'now' ? 'warn' : 'tag'}>{STATUS_LABEL[status]}</Tag>
              </div>
            );
          })}
        </div>
      </Box>
    </StageScreen>
  );
}
